import type { IconProps } from '@phosphor-icons/react';
import { User } from '@phosphor-icons/react';
import { forwardRef } from 'react';

/**
 * Fallback icon rendered by `Avatar` when it has no `children` and no (or a broken) image.
 *
 * MUI's built-in fallback is an internal Material `Person` SVG — this replaces it with the
 * brand Phosphor `User` (Phosphor-only icon rule, docs/ICONS.md; same approved-exception
 * pattern as Chip.deleteIcon and Autocomplete's popup/clear icons).
 *
 * Wired via `MuiAvatar.defaultProps.slots.fallback`. MUI passes `ownerState` to slot
 * components; Phosphor icons spread unknown props onto the `<svg>`, so we strip it here to
 * avoid a React "unknown prop" warning. Sizing comes from MUI's `.MuiAvatar-fallback` class
 * (width/height 75%), applied via the forwarded `className`.
 */
type AvatarFallbackProps = IconProps & { ownerState?: { alt?: string } };

export const AvatarFallback = forwardRef<SVGSVGElement, AvatarFallbackProps>(function AvatarFallback(
  { ownerState, ...props },
  ref
) {
  // Keep the Avatar's `alt` as the accessible name when the image broke (MUI drops the <img>
  // and its alt entirely); with no alt the fallback icon is decorative.
  const alt = ownerState?.alt;
  return (
    <User
      ref={ref}
      role={alt ? 'img' : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
      {...props}
    />
  );
});
